import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export function RoundedSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  className,
  searchable = false,
  searchPlaceholder = 'Search...',
  menuMaxHeight,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(undefined);
  const rootRef = useRef(null);
  const instanceIdRef = useRef(`rs_${Math.random().toString(36).slice(2)}`);

  const selected = useMemo(() => options.find((opt) => opt.value === value) || null, [options, value]);
  const label = selected?.label || placeholder || 'Select';

  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    const onAnotherSelectOpened = (event) => {
      if (!event?.detail?.id) return;
      if (event.detail.id === instanceIdRef.current) return;
      setOpen(false);
    };
    document.addEventListener('rounded-select:open', onAnotherSelectOpened);
    return () => {
      document.removeEventListener('rounded-select:open', onAnotherSelectOpened);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setMenuStyle(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const recomputeMenuPlacement = () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;

      const rect = rootEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const gap = 8;
      const safety = 12;
      const estimatedMenuHeight = menuMaxHeight || 320;

      const spaceBelow = viewportHeight - rect.bottom - safety;
      const spaceAbove = rect.top - safety;
      const openUp = spaceBelow < Math.min(180, estimatedMenuHeight) && spaceAbove > spaceBelow;
      const maxAvailableHeight = Math.max(120, openUp ? spaceAbove - gap : spaceBelow - gap);
      const width = Math.max(160, rect.width);

      const style = {
        top: openUp ? 'auto' : `calc(100% + ${gap}px)`,
        bottom: openUp ? `calc(100% + ${gap}px)` : 'auto',
        left: 0,
        right: 'auto',
        width: `${width}px`,
        maxHeight: `${Math.floor(maxAvailableHeight)}px`,
        overflowY: 'auto',
      };

      // Keep menu inside right viewport edge for very narrow layouts.
      if (rect.left + width > viewportWidth - safety) {
        style.left = 'auto';
        style.right = 0;
      }

      setMenuStyle(style);
    };

    recomputeMenuPlacement();
    window.addEventListener('resize', recomputeMenuPlacement);
    window.addEventListener('scroll', recomputeMenuPlacement, true);
    return () => {
      window.removeEventListener('resize', recomputeMenuPlacement);
      window.removeEventListener('scroll', recomputeMenuPlacement, true);
    };
  }, [open, menuMaxHeight]);

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((opt) => String(opt.label || '').toLowerCase().includes(normalized));
  }, [options, query, searchable]);

  return (
    <div ref={rootRef} className={className ? `rounded-select ${className}` : 'rounded-select'}>
      <button
        type="button"
        className="rounded-select-trigger"
        onClick={() => {
          if (disabled) return;
          setOpen((current) => {
            const next = !current;
            if (next) {
              document.dispatchEvent(
                new CustomEvent('rounded-select:open', { detail: { id: instanceIdRef.current } })
              );
            }
            return next;
          });
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={selected ? 'rounded-select-label' : 'rounded-select-label placeholder'}>{label}</span>
        <span className={open ? 'rounded-select-caret open' : 'rounded-select-caret'} aria-hidden="true" />
      </button>

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            className="rounded-select-menu"
            role="listbox"
            style={menuStyle || (menuMaxHeight ? { maxHeight: menuMaxHeight, overflowY: 'auto' } : undefined)}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {searchable && (
              <div className="rounded-select-search">
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={searchPlaceholder}
                />
              </div>
            )}
            {filteredOptions.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                className={opt.value === value ? 'rounded-select-option selected' : 'rounded-select-option'}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.label}
              </button>
            ))}
            {filteredOptions.length === 0 && <div className="rounded-select-empty">No results</div>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

